import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useBetonMatieres, useDeleteMatiere } from '@/hooks/useBetonCatalog';
import { MatiereFormDialog } from '@/components/operations/beton/MatiereFormDialog';
import { formatFCFA, formatNumber } from '@/utils/currency';
import type { MatierePremiere } from '@/types';
import { demander } from '@/components/ui/Fenetres';

export const MatieresPremieresPage = () => {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MatierePremiere | undefined>();

  const { data, isLoading } = useBetonMatieres({
    search: search || undefined,
    actif: showInactive ? undefined : true,
    limit: 200,
  });
  const remove = useDeleteMatiere();

  const openEdit = (m: MatierePremiere) => { setEditing(m); setDialogOpen(true); };
  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };

  const handleDelete = async (m: MatierePremiere) => {
    if (!(await demander.confirmer({ titre: `Supprimer "${m.code}" ?`, message: 'Si la MP est utilisée dans un BOM, elle sera désactivée à la place.', confirmer: 'Supprimer', danger: true }))) return;
    await remove.mutateAsync(m.id);
  };

  return (
    <>
      <Header
        title="Matières premières"
        subtitle="Catalogue des MP utilisées dans les nomenclatures (BOM) des produits"
        actions={
          <>
            <Link to="/operations/catalogue">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="size-4" /> Retour catalogue
              </Button>
            </Link>
            <Button size="sm" onClick={openCreate}>
              <Plus className="size-4" /> Nouvelle MP
            </Button>
          </>
        }
      />

      <div className="space-y-4 p-6">
        <Card>
          <CardContent className="!pt-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par code ou nom…"
                  className="pl-9"
                />
              </div>
              <label className="flex items-center gap-2 px-2 text-xs text-gray-600">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                Inclure inactives
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="!p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Code</th>
                    <th className="px-4 py-3 text-left font-medium">Nom</th>
                    <th className="px-4 py-3 text-left font-medium">Unité</th>
                    <th className="px-4 py-3 text-right font-medium">Prix unitaire HT</th>
                    <th className="px-4 py-3 text-right font-medium">Stock</th>
                    <th className="px-4 py-3 text-left font-medium">Fournisseur</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isLoading && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">Chargement…</td></tr>
                  )}
                  {!isLoading && data?.data.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <p className="text-sm text-gray-500">Aucune matière première</p>
                        <Button size="sm" className="mt-3" onClick={openCreate}>
                          <Plus className="size-4" /> Créer la première MP
                        </Button>
                      </td>
                    </tr>
                  )}
                  {!isLoading && data?.data.map((m) => {
                    const stockLow = parseFloat(m.stockActuel) < parseFloat(m.seuilAlerte);
                    return (
                      <tr key={m.id} className={!m.actif ? 'opacity-50' : ''}>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.code}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{m.nom}</div>
                          {m.description && <div className="text-xs text-gray-500">{m.description}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs uppercase text-gray-500">{m.unite}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatFCFA(m.prixUnitaireCents)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={stockLow ? 'flex items-center justify-end gap-1 text-amber-600' : 'tabular-nums text-gray-700'}>
                            {stockLow && <AlertTriangle className="size-3" />}
                            {formatNumber(parseFloat(m.stockActuel))}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{m.fournisseurNom ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => openEdit(m)}
                              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              aria-label="Modifier"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(m)}
                              className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              aria-label="Supprimer"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {data && (
          <p className="text-xs text-gray-500">
            <strong className="text-gray-900">{data.meta.total}</strong> matière{data.meta.total > 1 ? 's' : ''} premières
          </p>
        )}
      </div>

      <MatiereFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} matiere={editing} />
    </>
  );
};
